import { oc } from "@orpc/contract";
import { z } from "zod";
import { BackgroundJobStatus } from "@erp/shared-types";

/**
 * One piece of long-running work the signed-in user started.
 *
 * Read-only on purpose. Jobs are created by whichever flow starts the work
 * (uploading a pricelist, say) rather than by a generic "create job" call, so
 * there is nothing here that lets a client invent one.
 */
export const BackgroundJobSchema = z.object({
  id: z.uuid(),
  /**
   * What kind of work this is, e.g. "pricelist_extraction". A plain string, not
   * an enum: a client polling across a deploy may legitimately be handed a kind
   * it has never heard of, and must fall back rather than fail validation.
   */
  kind: z.string(),
  status: z.enum(BackgroundJobStatus),
  /** 0-100 when a real fraction is known; null means indeterminate. */
  percent: z.number().nullable(),
  /**
   * Structured detail for rendering, e.g. `{ fileName }`.
   *
   * Not a ready-made label — the server has no business knowing the reader's
   * locale, so the client composes the string from `kind` plus this.
   */
  payload: z.record(z.string(), z.unknown()),
  /** Present only on failure, and safe to show verbatim. */
  errorMessage: z.string().nullable(),
  attempts: z.number(),
  /** What the job acted on or produced, so a finished row can offer a link. */
  entityType: z.string().nullable(),
  entityId: z.uuid().nullable(),
  createdAt: z.date(),
  startedAt: z.date().nullable(),
  completedAt: z.date().nullable(),
});

export const ListBackgroundJobsInputSchema = z.object({
  /** How many of the newest jobs to return. */
  limit: z.number().int().min(1).max(100).default(25),
});

export const ListBackgroundJobsResponseSchema = z.object({
  jobs: z.array(BackgroundJobSchema),
});

export const GetBackgroundJobInputSchema = z.object({
  id: z.uuid(),
});

/** Null rather than an error when the job is unknown or someone else's. */
export const GetBackgroundJobResponseSchema = BackgroundJobSchema.nullable();

export const backgroundJobsContracts = {
  list: oc
    .input(ListBackgroundJobsInputSchema)
    .output(ListBackgroundJobsResponseSchema)
    .route({
      method: "GET",
      summary: "List the signed-in user's background jobs, newest first",
      description:
        "Returns the user's most recent long-running jobs, such as price list extractions, with status, progress percent, errors and the record each produced.",
    }),
  get: oc
    .input(GetBackgroundJobInputSchema)
    .output(GetBackgroundJobResponseSchema)
    .route({
      method: "GET",
      summary: "Get one of the signed-in user's background jobs",
      description:
        "Returns one job's status, progress, error and resulting record; returns null when the job is unknown or belongs to someone else.",
    }),
} as const;

export type BackgroundJobDto = z.infer<typeof BackgroundJobSchema>;
export type ListBackgroundJobsInput = z.infer<
  typeof ListBackgroundJobsInputSchema
>;
export type ListBackgroundJobsResponse = z.infer<
  typeof ListBackgroundJobsResponseSchema
>;
export type GetBackgroundJobInput = z.infer<typeof GetBackgroundJobInputSchema>;
export type GetBackgroundJobResponse = z.infer<
  typeof GetBackgroundJobResponseSchema
>;
